import { CssBaseline, ThemeProvider } from '@mui/material'
import { QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { BrowserRouter, useRoutes } from 'react-router-dom'

import { QueryErrorNotifier } from '@/components/QueryErrorNotifier'
import { ConfirmProvider } from '@/contexts/ConfirmProvider'
import { SnackbarProvider } from '@/contexts/SnackbarProvider'
import { queryClient } from '@/query/queryClient'
import { routes } from '@/routes'
import { startTokenRefresh } from '@/services/tokenRefresh'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { getTheme } from '@/themes/theme'

const AppRoutes = () => {
  return useRoutes(routes)
}

const App = () => {
  const mode = useThemeStore((state) => state.mode)
  const theme = useMemo(() => getTheme(mode), [mode])
  const initializeAuth = useAuthStore((state) => state.initializeAuth)

  useEffect(() => {
    // START FIREBASE SESSION TRACKING AND PERIODIC TOKEN REFRESH
    const stopAuthListener = initializeAuth()
    const refreshInterval = startTokenRefresh()
    return () => {
      stopAuthListener()
      clearInterval(refreshInterval)
    }
  }, [initializeAuth])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider>
          <QueryErrorNotifier />
          <ConfirmProvider>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <AppRoutes />
            </BrowserRouter>
          </ConfirmProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
